import classNames from 'classnames';
import cls from 'classnames';
import { useTrans } from 'core/hooks';
import actions from 'core/redux/features/products/actions';
import {
  selectActiveProductId,
  selectFilteredProductIds,
} from 'core/redux/features/products/reducers/products';
import { IProduct } from 'core/redux/features/products/types';
import { ProductId } from 'core/redux/features/shared/types';
import { AppState } from 'core/redux/reducers';
import { voidCallback } from 'core/utils';
import { locationParser } from 'core/utils/location';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { usePrevious, useSelectViewModes } from '../hooks';

interface IOwnProps {
  onSelectProduct?: (e: React.SyntheticEvent, id: ProductId) => void;
  children: (product: IProduct, arrIndex: number) => React.ReactNode;
}

export function ProductList({
  children,
  onSelectProduct: selectProduct,
}: IOwnProps) {
  const dispatch = useDispatch();

  const activeProductId = useSelector(selectActiveProductId);
  const trans = useTrans();
  const { products, deviceType, viewInfo, productIds } = useSelector(
    (state: AppState) => ({
      products: state.products.entities.products,
      viewInfo: state.products.viewInfo,
      deviceType: state.deviceType,
      productIds: selectFilteredProductIds(state),
    }),
  );

  const { viewMode } = useSelectViewModes();
  const location = useLocation();
  const history = useHistory();
  const onSelectProduct = useCallback(
    (product: IProduct) => {
      const { slug, id } = product;

      dispatch(
        actions.activateProduct({
          id,
          slug,
        }),
      );

      if (viewInfo || deviceType === 'mobile') {
        const hashRoute =
          deviceType !== 'mobile' && viewMode.id !== 'desc'
            ? viewMode.route
            : '';
        const loc = locationParser(location).setHash(hashRoute);
        loc.setPathName(
          trans(`routes.${product.product_type}`, {
            slug: product.slug,
          }),
        );

        history.push(loc.toString());
      }
    },
    [
      deviceType,
      dispatch,
      history,
      location,
      trans,
      viewInfo,
      viewMode.id,
      viewMode.route,
    ],
  );

  const refIsFilterApplied = useRef(false);

  let firstProduct: IProduct | null = null;
  if (productIds.length > 0) {
    firstProduct = products[productIds[0].id];
  }

  const lastFirstProduct = usePrevious(productIds[0]?.id);
  /**
   * Auto select first product on first load
   */

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !firstProduct ||
      deviceType === 'mobile'
    ) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (
      !refIsFilterApplied.current &&
      (searchParams.get('areas') || searchParams.get('tags'))
    ) {
      onSelectProduct(firstProduct);
    } else if (lastFirstProduct !== firstProduct.id && !viewInfo) {
      onSelectProduct(firstProduct);
    }

    refIsFilterApplied.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstProduct, deviceType]);

  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (deviceType === 'mobile') {
      setShowAll(true);
    }
  }, [deviceType]);

  let numOfActive = 0;
  return (
    <>
      {productIds.map((info, arrIndex) => {
        const product = products[info.id];
        const shouldCount =
          ['soldout', 'in-stock'].indexOf(product.status) > -1;

        const shouldHide = !shouldCount && numOfActive >= 30;

        // product is active
        // start counting when active product is less than 30
        if (shouldCount || (!shouldCount && numOfActive < 30)) {
          numOfActive++;
        }

        const route = trans(`routes.${product.product_type}`, {
          slug: product.slug,
        });
        return (
          <a
            key={info.id}
            onClick={e => {
              e.preventDefault();
              const { id } = product;

              if (selectProduct) {
                selectProduct(e, id);
              }

              onSelectProduct(product);
            }}
            className={classNames(
              'item',
              'item--' + info.id,
              shouldHide && !showAll && 'hide',
              info.result.length > 0 && 'is-not-active',
              activeProductId && activeProductId === info.id && 'is-active',
            )}
            href={route}
          >
            {children(product, arrIndex)}
          </a>
        );
      })}
      {productIds.length > 30 && (
        <div
          className="is-flex-mid is-full show-all-resto"
          style={{ display: deviceType === 'mobile' ? 'none' : '' }}
          onClick={e => {
            e.preventDefault();
            setShowAll(!showAll);
          }}
        >
          <div className="is-cursor-pointer has-text-centered line-height-1 has-text-red-dark">
            <div
              style={{ marginBottom: '5px' }}
              className={cls('eb', showAll ? 'eb-ru' : 'hide')}
            />
            <div className="show-all-resto__label">
              {trans('show_all_resto')}
            </div>
            <i className={cls('eb', !showAll && 'eb-rd')} />
          </div>
        </div>
      )}
    </>
  );
}

ProductList.defaultProps = {
  onSelectProduct: voidCallback,
};

// product name list
export default memo(ProductList);
// let configureNames = connect(mapStateToProps, mapDispatchToProps)(NameListView)
//
// export default withRouter(configureNames)
